import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../services/api';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const fetchDailyCollection = (date) => api.get('/reports/daily-collection', { params: { date } }).then(res => res.data.data);
const fetchAging = () => api.get('/reports/outstanding-aging').then(res => res.data.data);

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6366f1', '#8b5cf6'];

export default function Reports() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: dailyData } = useQuery(['daily-collection', date], () => fetchDailyCollection(date));
  const { data: agingData } = useQuery('outstanding-aging', fetchAging);

  const daily = dailyData || {};
  const aging = agingData?.aging || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="mt-1 text-sm text-slate-500">Financial and operational reports</p>
      </div>

      {/* Daily Collection */}
      <div className="card">
        <div className="card-header flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h3 className="text-lg font-medium">Daily Collection Report</h3>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input-field w-full sm:w-auto"
            />
            <button className="btn-secondary w-full sm:w-auto">
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-500">Total Amount</p>
              <p className="text-2xl font-bold text-slate-900">PKR {(daily.summary?.totalAmount || 0).toLocaleString()}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-500">Total Transactions</p>
              <p className="text-2xl font-bold text-slate-900">{daily.summary?.totalCount || 0}</p>
            </div>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Transaction #</th>
                  <th>Challan</th>
                  <th>Consumer</th>
                  <th>Amount</th>
                  <th>Channel</th>
                </tr>
              </thead>
              <tbody>
                {daily.transactions?.map((txn) => (
                  <tr key={txn.id}>
                    <td className="font-mono text-xs">{txn.transactionNumber}</td>
                    <td>{txn.challanNo}</td>
                    <td>{txn.bill?.consumerName}</td>
                    <td className="font-medium">PKR {parseFloat(txn.paymentAmount).toLocaleString()}</td>
                    <td>{txn.bankChannel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Aging Report */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Outstanding Aging Report</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={aging}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ bucket, count }) => `${bucket}: ${count}`}
                    outerRadius={80}
                    dataKey="count"
                  >
                    {aging.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Age Bucket</th>
                    <th>Count</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {aging.map((item, index) => (
                    <tr key={index}>
                      <td>{item.bucket}</td>
                      <td>{item.count}</td>
                      <td className="font-medium">PKR {item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
